import { AccountStatusEnum, IAccountRespSchema } from '@monetize/types/app';
import { createColumnHelper } from '../../components/Monetize/DataTableNew/appTable';
import { PastDueIndicatorIcon } from '../../components/PastDueIndicatorIcon';
import { actionCatalogColumn } from '../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  checkboxBodyTemplate,
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  getCurrencyFilterValues,
  nameWithIdBodyTemplate,
  textBodyTemplate,
} from '../../components/Monetize/DataTableNew/tableUtilsNew';
import { AccountStatusDisplayText } from '../../constants/accounts';
import { getAccountDetailRoute } from '../../constants/routes';

const columnHelper = createColumnHelper<IAccountRespSchema>();

export interface AccountListColumnContext extends ColumnContextBase<IAccountRespSchema> {
  showPastDue: boolean;
}

/** tableId: FilterStateKeys.ACCOUNT_LIST */
export const ACCOUNT_LIST_CATALOG: ColumnCatalog<
  IAccountRespSchema,
  AccountListColumnContext
> = [
  {
    id: '_PAST_DUE_ICON_',
    label: '',
    customizable: false,
    isAvailable: ({ showPastDue }) => showPastDue,
    build: () =>
      columnHelper.display({
        id: '_PAST_DUE_ICON_',
        header: '',
        size: 5,
        cell: (info) => {
          const data = info.row.original;
          return (
            <PastDueIndicatorIcon
              pastDueInvoiceCount={data.pastDueInvoiceCount}
              pastDueAmount={data.pastDueAmountOutstanding}
              minimumInvoiceDueDate={data.minimumInvoiceDueDate}
              currency={data.defaultCurrency}
            />
          );
        },
      }),
  },
  {
    id: 'accountName',
    label: 'Name',
    customizable: false,
    build: () =>
      columnHelper.accessor('accountName', {
        ...getColHeaderWithSize('Name', 300),
        ...getColHeaderFilters('TEXT'),
        cell: nameWithIdBodyTemplate<IAccountRespSchema, unknown>({
          idProp: 'id',
          nameProp: 'accountName',
          idLinkFn: (id) => getAccountDetailRoute(id),
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'defaultCurrency',
    label: 'Currency',
    build: () =>
      columnHelper.accessor('defaultCurrency', {
        ...getColHeaderWithSize('Currency'),
        cell: textBodyTemplate(),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getCurrencyFilterValues(),
        }), // not supported yet
        enableSorting: true, // not supported yet
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            AccountStatusEnum,
            AccountStatusDisplayText,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: AccountStatusDisplayText,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'customId',
    label: 'Custom ID',
    build: () =>
      columnHelper.accessor('customId', {
        ...getColHeaderWithSize('Custom ID', 200),
        ...getColHeaderFilters('TEXT'),
        cell: textBodyTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        header: 'Updated',
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        size: 180,
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        header: 'Created',
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        size: 180,
        enableSorting: true,
      }),
  },
  {
    id: 'isTestAccount',
    label: 'Test Account',
    build: () =>
      columnHelper.accessor('isTestAccount', {
        header: 'Test Account',
        // Opt into persisted-value restoration on load (so the column shows
        // "No" while the default hide filter is active), but keep no custom
        // reset default. This lets "Reset All" clear the filter to "All" and
        // reveal test accounts instead of snapping back to "No". The
        // hide-by-default view comes from getDefaultFilter(ACCOUNT_LIST), not
        // from this column.
        ...getColHeaderFilters('BOOLEAN', {
          booleanFilter: {},
        }),
        cell: checkboxBodyTemplate(),
        size: 180,
      }),
  },
  actionCatalogColumn<IAccountRespSchema>(),
];
